import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Eye, Sun, Zap, Sparkles, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const Services = () => {
  const { t, i18n } = useTranslation();

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Naše služby',
      description: 'Profesionálna montáž slnečných ochranných fólií. Bezpečnostné fólie, fólie na súkromie, tepelnoizolačné fólie, smart fólie. Obhliadka zdarma.',
      keywords: 'montáž fólií, bezpečnostné fólie, fólie na súkromie, tepelnoizolačné fólie, smart fólie'
    },
    cs: {
      title: 'Naše služby',
      description: 'Profesionální montáž slunečních ochranných fólií. Bezpečnostní fólie, fólie na soukromí, tepelně izolační fólie, smart fólie.',
      keywords: 'montáž fólií, bezpečnostní fólie, fólie na soukromí, tepelně izolační fólie'
    },
    en: {
      title: 'Our Services',
      description: 'Professional window film installation. Security films, privacy films, thermal insulation films, smart switchable films. Free consultation.',
      keywords: 'film installation, security films, privacy films, thermal films, smart films'
    },
    de: {
      title: 'Unsere Dienstleistungen',
      description: 'Professionelle Montage von Sonnenschutzfolien. Sicherheitsfolien, Sichtschutzfolien, Wärmeisolierungsfolien, Smart-Folien.',
      keywords: 'Folienmontage, Sicherheitsfolien, Sichtschutzfolien, Wärmeisolierungsfolien'
    },
    ru: {
      title: 'Наши услуги',
      description: 'Профессиональный монтаж солнцезащитных пленок. Безопасные пленки, пленки для приватности, теплоизоляционные пленки, смарт-пленки.',
      keywords: 'монтаж пленок, безопасные пленки, пленки для приватности, теплоизоляционные пленки'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  const services = [
    {
      icon: Shield,
      titleKey: "services.service1_title",
      subtitleKey: "services.service1_subtitle",
      descKey: "services.service1_desc",
      benefits: [
        "services.service1_benefit1",
        "services.service1_benefit2",
        "services.service1_benefit3",
        "services.service1_benefit4"
      ]
    },
    {
      icon: Eye,
      titleKey: "services.service2_title",
      subtitleKey: "services.service2_subtitle",
      descKey: "services.service2_desc",
      benefits: [
        "services.service2_benefit1",
        "services.service2_benefit2",
        "services.service2_benefit3",
        "services.service2_benefit4"
      ]
    },
    {
      icon: Sun,
      titleKey: "services.service3_title",
      subtitleKey: "services.service3_subtitle",
      descKey: "services.service3_desc",
      benefits: [
        "services.service3_benefit1",
        "services.service3_benefit2",
        "services.service3_benefit3",
        "services.service3_benefit4"
      ]
    },
    {
      icon: Sparkles,
      titleKey: "services.service4_title",
      subtitleKey: "services.service4_subtitle",
      descKey: "services.service4_desc",
      benefits: [
        "services.service4_benefit1",
        "services.service4_benefit2",
        "services.service4_benefit3",
        "services.service4_benefit4"
      ]
    }
  ];

  const process = [
    {
      step: "01",
      titleKey: "services.process_step1_title",
      descKey: "services.process_step1_desc"
    },
    {
      step: "02",
      titleKey: "services.process_step2_title",
      descKey: "services.process_step2_desc"
    },
    {
      step: "03",
      titleKey: "services.process_step3_title",
      descKey: "services.process_step3_desc"
    }
  ];

  const values = [
    {
      icon: Zap,
      titleKey: "services.value1_title",
      descKey: "services.value1_desc"
    },
    {
      icon: Shield,
      titleKey: "services.value2_title",
      descKey: "services.value2_desc"
    },
    {
      icon: CheckCircle,
      titleKey: "services.value3_title",
      descKey: "services.value3_desc"
    },
    {
      icon: Sun,
      titleKey: "services.value4_title",
      descKey: "services.value4_desc"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/services"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6" data-testid="services-title">
            {t('services.title')}
          </h1>
          <p className="text-xl max-w-3xl">
            {t('services.hero_text')}
          </p>
        </div>
      </div>

      {/* Philosophy Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">{t('services.philosophy_title')}</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {t('services.philosophy_text1')}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('services.philosophy_text2')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{t('services.what_we_offer')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition">
                  <div className="p-8">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                      <Icon className="h-8 w-8 text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{t(service.titleKey)}</h3>
                    <p className="text-orange-500 font-semibold mb-4">{t(service.subtitleKey)}</p>
                    <p className="text-gray-700 mb-6">{t(service.descKey)}</p>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-lg">{t('services.benefits')}</h4>
                      {service.benefits.map((benefitKey, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{t(benefitKey)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{t('services.how_it_works')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold mb-4">{t(item.titleKey)}</h3>
                <p className="text-gray-600">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-br from-orange-500 to-yellow-400 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{t('services.why_us')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{t(value.titleKey)}</h3>
                  <p className="text-white/90">{t(value.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {t('services.cta_title')}
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            {t('services.cta_desc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+421233070865"
              className="px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
              data-testid="services-phone-btn"
            >
              +421 233 070 865
            </a>
            <a
              href="https://wa.me/421952411679"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition"
              data-testid="services-whatsapp-btn"
            >
              WhatsApp: +421 952 411 679
            </a>
            <a
              href="mailto:sunify.sk@gmail.com"
              className="px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition"
              data-testid="services-email-btn"
            >
              sunify.sk@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
